import { Helmet } from "react-helmet";
import { Footer } from "@/components/Footer";
import { EnterpriseIntegration } from "@/components/enterprise/EnterpriseIntegration";
import { ClinicalWorkflow } from "@/components/enterprise/ClinicalWorkflow";
import { ROICalculator } from "@/components/enterprise/ROICalculator";
import { CaseStudies } from "@/components/enterprise/CaseStudies";
import { ImplementationGuide } from "@/components/enterprise/ImplementationGuide";
import { Button } from "@/components/ui/button";
import { Building2 } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useState } from "react";
import { DemoScheduleForm } from "@/components/enterprise/DemoScheduleForm";

const Enterprise = () => {
  const navigate = useNavigate();
  const [showDemoForm, setShowDemoForm] = useState(false);

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "Product",
    "@id": "https://scienzahealth.com/enterprise#product",
    "name": "Scienza Health Enterprise Solutions",
    "description": "Enterprise-grade cognitive health monitoring and management platform for healthcare organizations",
    "brand": {
      "@type": "Brand",
      "name": "Scienza Health"
    },
    "category": "Healthcare Software",
    "applicationCategory": "Enterprise Healthcare Platform",
    "offers": {
      "@type": "Offer",
      "businessFunction": "https://schema.org/ProvideService",
      "itemOffered": {
        "@type": "SoftwareApplication",
        "name": "Scienza Health Enterprise Platform",
        "applicationCategory": "Healthcare",
        "operatingSystem": "Cloud-based"
      }
    },
    "hasProductionFeature": [
      "EHR Integration",
      "Clinical Workflow Optimization",
      "Enterprise Analytics",
      "HIPAA Compliance",
      "Multi-facility Management"
    ],
    "audience": {
      "@type": "Audience",
      "audienceType": ["Healthcare Organizations", "Hospitals", "Clinical Practices"]
    },
    "breadcrumb": {
      "@type": "BreadcrumbList",
      "itemListElement": [{
        "@type": "ListItem",
        "position": 1,
        "name": "Home",
        "item": "https://scienzahealth.com"
      }, {
        "@type": "ListItem",
        "position": 2,
        "name": "Enterprise Solutions",
        "item": "https://scienzahealth.com/enterprise"
      }]
    }
  };

  return (
    <>
      <Helmet>
        <title>Enterprise Solutions | Scienza Health - Healthcare Organization Platform</title>
        <meta 
          name="description" 
          content="Transform your healthcare organization with Scienza Health's enterprise solutions. Seamless EHR integration, clinical workflow optimization, and comprehensive cognitive health monitoring." 
        />
        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
      </Helmet>

      {/* Hero Section */}
      <div className="relative bg-gradient-to-b from-secondary/50 to-white">
        <div className="absolute inset-0 bg-grid-pattern opacity-5" />
        <div className="relative mobile-container mobile-hero">
          <div className="text-center space-y-6 sm:space-y-8">
            <div className="inline-flex h-16 w-16 sm:h-20 sm:w-20 items-center justify-center rounded-2xl bg-primary/10 mb-6 sm:mb-8 touch-target">
              <Building2 className="h-8 w-8 sm:h-10 sm:w-10 text-primary" />
            </div>
            <h1 className="mobile-text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-semibold text-gray-900 tracking-tight">
              Value-Based Care
              <span className="block text-primary">Enterprise Solutions<br />for Health Systems</span>
            </h1>
            <p className="mobile-text-lg sm:text-xl text-gray-600 max-w-2xl mx-auto">
              Comprehensive cognitive health monitoring and management platform designed for healthcare providers and organizations.
            </p>
            <div className="flex flex-col sm:flex-row justify-center gap-4">
              <Button 
                className="mobile-btn-lg"
                onClick={() => setShowDemoForm(true)}
              >
                Schedule Demo
              </Button>
              <Button 
                variant="outline" 
                className="mobile-btn-lg"
                onClick={() => navigate("/enterprise/contact-sales")}
              >
                Contact Sales
              </Button>
            </div>
          </div>
        </div>
      </div>

      <ClinicalWorkflow />
      <EnterpriseIntegration />
      <ROICalculator />
      <CaseStudies />
      <ImplementationGuide />
      
      <Footer />
      <DemoScheduleForm 
        open={showDemoForm} 
        onOpenChange={setShowDemoForm}
      />
    </>
  );
};

export default Enterprise;
